import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ComposedChart, Scatter, ScatterChart
} from 'recharts';
import { format, subDays, subMonths, startOfDay, endOfDay, parseISO } from 'date-fns';
import { 
    TrendingUp, TrendingDown, DollarSign, Package, Users, 
    Calendar, Filter, Download, BarChart3, PieChart as PieChartIcon,
    Activity, Target, Award, Clock, CheckCircle, AlertCircle, X, Search, FileText
} from 'lucide-react';
import AnalyticsDashboard from './AnalyticsDashboard';
import { fetchQuotesForOrders, subscribeToCacheUpdates } from '../utils/quotesService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// Custom Tooltip component matching the main page style
const CustomTooltip = ({ text, children }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            {children}
            {show && (
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 rounded bg-black text-white text-xs whitespace-nowrap z-50 shadow-lg">
                    {text}
                </span>
            )}
        </span>
    );
};

// Vendor Comparison Modal Component
const VendorComparisonModal = ({ allVendors, allOrders, quotesData, onClose }) => {
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [dateRange, setDateRange] = useState('30d');
    const [comparisonData, setComparisonData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCharts, setShowCharts] = useState(true);
    
    // Date range options
    const dateRangeOptions = [
        { label: 'Last 7 days', value: '7d' },
        { label: 'Last 30 days', value: '30d' },
        { label: 'Last 90 days', value: '90d' },
        { label: 'Last 6 months', value: '6m' },
        { label: 'Last 1 year', value: '1y' }
    ];
    
    // Filter vendors based on search
    const filteredVendors = useMemo(() => {
        if (!searchQuery) return allVendors || [];
        return (allVendors || []).filter(vendor => 
            vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (vendor.city && vendor.city.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [allVendors, searchQuery]);
    
    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);
    
    // Calculate comparison data
    const calculateComparisonData = useCallback(() => {
        if (selectedVendors.length === 0) return [];
        
        const now = new Date();
        let startDate;
        
        switch (dateRange) {
            case '7d':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case '30d':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case '90d':
                startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                break;
            case '6m':
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '1y':
                startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                break;
            default:
                startDate = new Date(0);
        }
        
        const vendorStats = selectedVendors.map(vendor => {
            // Get all orders for this vendor within date range
            const vendorOrders = allOrders.filter(order => {
                const orderDate = order.createdAt?.toDate?.() || new Date(0);
                return orderDate >= startDate;
            });
            
            // Get quotes for this vendor
            const vendorQuotes = vendorOrders
                .flatMap(order => 
                    (quotesData[order.id] || [])
                        .filter(quote => 
                            quote.supplier && 
                            quote.supplier.toLowerCase().includes(vendor.name.toLowerCase())
                        )
                        .map(quote => ({ ...quote, orderId: order.id, order }))
                );
            
            // Get completed orders for this vendor
            const completedOrders = vendorOrders.filter(order => 
                order.status === 'completed' && 
                (quotesData[order.id] || []).some(quote => 
                    quote.supplier && 
                    quote.supplier.toLowerCase().includes(vendor.name.toLowerCase())
                )
            );
            
            // Calculate metrics based on completed orders
            const totalQuotes = vendorQuotes.length;
            const totalValue = vendorQuotes.reduce((sum, quote) => sum + (quote.price || 0), 0);
            const avgPrice = totalQuotes > 0 ? totalValue / totalQuotes : 0;
            
            // Win rate based on completed orders: orders where this vendor won
            const wonOrders = completedOrders.filter(order => {
                const orderQuotes = quotesData[order.id] || [];
                const vendorOrderQuotes = orderQuotes.filter(quote => 
                    quote.supplier && 
                    quote.supplier.toLowerCase().includes(vendor.name.toLowerCase())
                );
                return vendorOrderQuotes.some(quote => quote.isBestQuote === true);
            }).length;
            
            const winRate = completedOrders.length > 0 ? (wonOrders / completedOrders.length) * 100 : 0;
            
            // Additional metrics
            const pendingQuotes = vendorQuotes.filter(q => q.status === 'under_negotiation').length;
            const completedQuotes = vendorQuotes.filter(q => q.status === 'completed').length;
            
            return {
                vendor,
                totalQuotes,
                totalValue,
                avgPrice,
                winRate,
                wonOrders,
                completedOrders: completedOrders.length,
                pendingQuotes,
                completedQuotes,
                quotes: vendorQuotes
            };
        });
        
        // Find highest win rate and best performance
        const sortedByWinRate = [...vendorStats].sort((a, b) => b.winRate - a.winRate);
        const sortedByPerformance = [...vendorStats].sort((a, b) => {
            // Performance score: 70% win rate + 30% total value (normalized)
            const maxValue = Math.max(...vendorStats.map(v => v.totalValue), 1);
            const aScore = (a.winRate * 0.7) + ((a.totalValue / maxValue) * 30);
            const bScore = (b.winRate * 0.7) + ((b.totalValue / maxValue) * 30);
            return bScore - aScore;
        });
        
        const highestWinRate = sortedByWinRate[0]?.winRate || 0;
        const bestPerformance = sortedByPerformance[0]?.vendor?.name || '';
        
        // Add comparison flags
        return vendorStats.map(stat => ({
            ...stat,
            isHighestWinRate: stat.winRate === highestWinRate && stat.winRate > 0,
            isBestPerformance: stat.vendor.name === bestPerformance && stat.winRate > 0
        })).sort((a, b) => b.totalValue - a.totalValue);
    }, [selectedVendors, dateRange, allOrders, quotesData]);
    
    useEffect(() => {
        setComparisonData(calculateComparisonData());
    }, [calculateComparisonData]);
    
    const handleVendorToggle = (vendor) => {
        setSelectedVendors(prev => 
            prev.find(v => v.id === vendor.id) 
                ? prev.filter(v => v.id !== vendor.id)
                : [...prev, vendor]
        );
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
            <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="bg-black p-6 text-white">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-3xl font-bold">Vendor Comparison</h2>
                            <p className="text-gray-300 mt-1">Compare vendor performance and make data-driven decisions</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>
                
                <div className="p-6 space-y-6 max-h-[calc(95vh-120px)] overflow-y-auto">
                    {/* Controls Section */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Vendor Selection */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                        <Users size={20} className="text-blue-600" />
                                        Select Vendors (2-5 vendors)
                                    </h3>
                                    <span className="text-sm text-gray-500">
                                        {selectedVendors.length}/5 selected
                                    </span>
                                </div>
                                
                                {/* Search Bar */}
                                <div className="relative mb-4">
                                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search vendors by name or city..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                
                                {/* Vendor List */}
                                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                                    {filteredVendors.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500">
                                            No vendors found
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {filteredVendors.map(vendor => {
                                                const isSelected = selectedVendors.find(v => v.id === vendor.id);
                                                const isDisabled = selectedVendors.length >= 5 && !isSelected;
                                                
                                                return (
                                                    <label 
                                                        key={vendor.id} 
                                                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                                                            isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 
                                                            isDisabled ? 'bg-gray-50 cursor-not-allowed opacity-50' : 
                                                            'hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={!!isSelected}
                                                            onChange={() => handleVendorToggle(vendor)}
                                                            disabled={isDisabled}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-900">{vendor.name}</div>
                                                            {vendor.city && (
                                                                <div className="text-sm text-gray-500">{vendor.city}</div>
                                                            )}
                                                        </div>
                                                        {isSelected && (
                                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Date Range & Options */}
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <Calendar size={20} className="text-green-600" />
                                        Date Range
                                    </h3>
                                    <div className="grid grid-cols-1 gap-2">
                                        {dateRangeOptions.map(option => (
                                            <button
                                                key={option.value}
                                                onClick={() => setDateRange(option.value)}
                                                className={`p-3 rounded-lg border-2 transition-all text-left ${
                                                    dateRange === option.value
                                                        ? 'border-green-500 bg-green-50 text-green-700'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="font-medium">{option.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* View Options */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <BarChart3 size={20} className="text-purple-600" />
                                        View Options
                                    </h3>
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={showCharts}
                                                onChange={(e) => setShowCharts(e.target.checked)}
                                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            <span className="text-sm text-gray-700">Show charts and graphs</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Comparison Results */}
                    {selectedVendors.length >= 2 && (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-3xl font-bold text-blue-800">
                                                {comparisonData.reduce((sum, vendor) => sum + vendor.totalQuotes, 0)}
                                            </div>
                                            <div className="text-sm text-blue-600 font-medium">Total Quotes</div>
                                        </div>
                                        <div className="p-3 bg-blue-200 rounded-lg">
                                            <FileText size={24} className="text-blue-600" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-3xl font-bold text-green-800">
                                                ₹{comparisonData.reduce((sum, vendor) => sum + vendor.totalValue, 0).toLocaleString('en-IN')}
                                            </div>
                                            <div className="text-sm text-green-600 font-medium">Total Value</div>
                                        </div>
                                        <div className="p-3 bg-green-200 rounded-lg">
                                            <DollarSign size={24} className="text-green-600" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-3xl font-bold text-purple-800">
                                                {comparisonData.length > 0 ? 
                                                    (comparisonData.reduce((sum, vendor) => sum + vendor.winRate, 0) / comparisonData.length).toFixed(1) : 0}%
                                            </div>
                                            <div className="text-sm text-purple-600 font-medium">Avg Win Rate</div>
                                        </div>
                                        <div className="p-3 bg-purple-200 rounded-lg">
                                            <Target size={24} className="text-purple-600" />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-3xl font-bold text-orange-800">
                                                {comparisonData.length}
                                            </div>
                                            <div className="text-sm text-orange-600 font-medium">Vendors</div>
                                        </div>
                                        <div className="p-3 bg-orange-200 rounded-lg">
                                            <Users size={24} className="text-orange-600" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Charts Section */}
                            {showCharts && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Quote Volume Chart */}
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Quote Volume Comparison</h4>
                                        <div className="space-y-3">
                                            {comparisonData.map((vendorData, index) => {
                                                const maxQuotes = Math.max(...comparisonData.map(v => v.totalQuotes));
                                                const percentage = maxQuotes > 0 ? (vendorData.totalQuotes / maxQuotes) * 100 : 0;
                                                
                                                return (
                                                    <div key={vendorData.vendor.id} className="space-y-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm font-medium text-gray-700">{vendorData.vendor.name}</span>
                                                            <span className="text-sm text-gray-500">{vendorData.totalQuotes} quotes</span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                                            <div 
                                                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    
                                    {/* Value Comparison Chart */}
                                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Value Comparison</h4>
                                        <div className="space-y-3">
                                            {comparisonData.map((vendorData, index) => {
                                                const maxValue = Math.max(...comparisonData.map(v => v.totalValue));
                                                const percentage = maxValue > 0 ? (vendorData.totalValue / maxValue) * 100 : 0;
                                                
                                                return (
                                                    <div key={vendorData.vendor.id} className="space-y-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm font-medium text-gray-700">{vendorData.vendor.name}</span>
                                                            <span className="text-sm text-gray-500">₹{vendorData.totalValue.toLocaleString('en-IN')}</span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                                            <div 
                                                                className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                                                                style={{ width: `${percentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Enhanced Comparison Table */}
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-800">Detailed Comparison</h4>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Vendor
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Quotes
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total Value
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Avg Price
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Win Rate
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Performance
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {comparisonData.map((vendorData, index) => {
                                                const isTopPerformer = index === 0;
                                                const winRateColor = vendorData.winRate >= 70 ? 'text-green-600' : 
                                                                   vendorData.winRate >= 50 ? 'text-yellow-600' : 'text-red-600';
                                                                
                                                return (
                                                    <tr key={vendorData.vendor.id} className={`${isTopPerformer ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0">
                                                                    <div className={`w-3 h-3 rounded-full ${isTopPerformer ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                                                                </div>
                                                                <div className="ml-3">
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {vendorData.vendor.name}
                                                                        {isTopPerformer && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Top Performer</span>}
                                                                    </div>
                                                                    {vendorData.vendor.city && (
                                                                        <div className="text-sm text-gray-500">{vendorData.vendor.city}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {vendorData.totalQuotes}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                            ₹{vendorData.totalValue.toLocaleString('en-IN')}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            ₹{vendorData.avgPrice.toLocaleString('en-IN')}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="space-y-1">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className={`text-sm font-medium ${winRateColor}`}>
                                                                        {vendorData.winRate.toFixed(1)}%
                                                                    </span>
                                                                    {vendorData.isHighestWinRate && (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            🏆 Best
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {vendorData.wonOrders} won / {vendorData.completedOrders} completed orders
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="space-y-2">
                                                                <div className="flex items-center">
                                                                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                                                        <div 
                                                                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                                                                            style={{ width: `${Math.min(vendorData.winRate, 100)}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className="text-xs text-gray-500">
                                                                        {vendorData.winRate >= 70 ? 'Excellent' : 
                                                                         vendorData.winRate >= 50 ? 'Good' : 'Needs Improvement'}
                                                                    </span>
                                                                    {vendorData.isBestPerformance && (
                                                                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                            ⭐ Top
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {vendorData.completedOrders} completed orders, {vendorData.totalQuotes} total quotes
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedVendors.length < 2 && (
                        <div className="text-center py-12">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Users size={32} className="text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Vendors to Compare</h3>
                            <p className="text-gray-500">Choose at least 2 vendors from the list above to start comparing their performance.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SalesAnalytics = ({ allOrders, allVendors, currentUser }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [dateRange, setDateRange] = useState('30d');
    const [selectedMetric, setSelectedMetric] = useState('revenue');
    const [chartType, setChartType] = useState('line');
    const [quotesData, setQuotesData] = useState({});
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(true);
    const [loadedQuotesCount, setLoadedQuotesCount] = useState(0);
    const [totalQuotesToLoad, setTotalQuotesToLoad] = useState(0);
    
    // Vendor comparison state
    const [showVendorComparison, setShowVendorComparison] = useState(false);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [comparisonDateRange, setComparisonDateRange] = useState('30d');
    const [comparisonQuotes, setComparisonQuotes] = useState([]);

    // Dropdown state
    const [showDateRangeDropdown, setShowDateRangeDropdown] = useState(false);
    const [showMetricDropdown, setShowMetricDropdown] = useState(false);
    const dateRangeDropdownRef = useRef(null);
    const metricDropdownRef = useRef(null);

    // Click outside handler for dropdowns
    const useClickOutside = (ref, callback) => {
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (ref.current && !ref.current.contains(event.target)) {
                    callback();
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [ref, callback]);
    };

    useClickOutside(dateRangeDropdownRef, () => setShowDateRangeDropdown(false));
    useClickOutside(metricDropdownRef, () => setShowMetricDropdown(false));

    // Dropdown options
    const dateRangeOptions = [
        { label: 'Last 7 days', value: '7d' },
        { label: 'Last 30 days', value: '30d' },
        { label: 'Last 90 days', value: '90d' },
        { label: 'Last 6 months', value: '6m' },
        { label: 'Last 1 year', value: '1y' }
    ];

    const metricOptions = [
        { label: 'Revenue', value: 'revenue' },
        { label: 'Orders', value: 'orders' },
        { label: 'Completed Orders', value: 'completed' },
        { label: 'Pending Orders', value: 'pending' },
        { label: 'Average Order Value', value: 'avgOrderValue' }
    ];

    const getCurrentDateRangeLabel = () => {
        const option = dateRangeOptions.find(opt => opt.value === dateRange);
        return option ? option.label : 'Last 30 days';
    };

    const getCurrentMetricLabel = () => {
        const option = metricOptions.find(opt => opt.value === selectedMetric);
        return option ? option.label : 'Revenue';
    };

    // Optimized quotes fetching using shared service
    const fetchQuotesData = useCallback(async () => {
        if (allOrders.length === 0) {
            setIsLoadingQuotes(false);
            return;
        }

        setIsLoadingQuotes(true);
        setLoadedQuotesCount(0);
        
        try {
            const quotesMap = await fetchQuotesForOrders(allOrders, (loaded, total) => {
                setLoadedQuotesCount(loaded);
                setTotalQuotesToLoad(total);
            });
            
            setQuotesData(quotesMap);
        } catch (error) {
            console.error('Error fetching quotes:', error);
        } finally {
            setIsLoadingQuotes(false);
        }
    }, [allOrders]);

    // Subscribe to cache updates
    useEffect(() => {
        const unsubscribe = subscribeToCacheUpdates((orderId, quotes) => {
            setQuotesData(prev => ({
                ...prev,
                [orderId]: quotes
            }));
        });

        return unsubscribe;
    }, []);

    // Fetch quotes data on mount and when orders change
    useEffect(() => {
        fetchQuotesData();
    }, [fetchQuotesData]);

    // Calculate analytics data with memoization
    const analyticsData = useMemo(() => {
        if (!allOrders || !allVendors) return {};

        const now = new Date();
        let startDate;
        
        switch (dateRange) {
            case '7d':
                startDate = subDays(now, 7);
                break;
            case '30d':
                startDate = subDays(now, 30);
                break;
            case '90d':
                startDate = subDays(now, 90);
                break;
            case '6m':
                startDate = subMonths(now, 6);
                break;
            case '1y':
                startDate = subMonths(now, 12);
                break;
            default:
                startDate = subDays(now, 30);
        }

        // Filter orders by date range
        const filteredOrders = allOrders.filter(order => {
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
            return orderDate >= startDate && orderDate <= now;
        });

        // Calculate daily data for charts
        const dailyData = {};
        const currentDate = new Date(startDate);
        
        while (currentDate <= now) {
            const dateKey = format(currentDate, 'yyyy-MM-dd');
            dailyData[dateKey] = {
                date: dateKey,
                revenue: 0,
                orders: 0,
                completed: 0,
                pending: 0,
                avgOrderValue: 0
            };
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Populate daily data
        filteredOrders.forEach(order => {
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
            const dateKey = format(orderDate, 'yyyy-MM-dd');
            
            if (dailyData[dateKey]) {
                dailyData[dateKey].orders += 1;
                
                // Calculate revenue from quotes for this order (for placed orders)
                if (order.status === 'In Progress' || order.status === 'Completed') {
                    const orderQuotes = quotesData[order.id] || [];
                    const totalValue = orderQuotes.reduce((sum, quote) => {
                        return sum + (parseFloat(quote.price) || 0);
                    }, 0);
                    
                    dailyData[dateKey].revenue += totalValue;
                }
                
                if (order.status === 'Completed') {
                    dailyData[dateKey].completed += 1;
                } else if (order.status === 'Awaiting Requirements' || order.status === 'Negotiation') {
                    dailyData[dateKey].pending += 1;
                }
            }
        });

        // Calculate average order values
        Object.keys(dailyData).forEach(date => {
            if (dailyData[date].orders > 0) {
                dailyData[date].avgOrderValue = dailyData[date].revenue / dailyData[date].orders;
            }
        });

        // Helper for robust normalization
        function normalizeName(name) {
          if (typeof name !== 'string') return '';
          return name.trim().toLowerCase().replace(/\s+/g, ' ');
        }
        
        // Vendor performance data - for placed orders
        const vendorPerformance = {};
        allVendors.forEach(vendor => {
            const vendorOrders = filteredOrders.filter(order => {
                if (order.status !== 'In Progress' && order.status !== 'Completed') return false;
                const orderQuotes = quotesData[order.id] || [];
                return orderQuotes.some(quote => quote.vendorId === vendor.id);
            });

            const totalQuotes = vendorOrders.reduce((sum, order) => {
                const orderQuotes = quotesData[order.id] || [];
                return sum + orderQuotes.filter(q => q.vendorId === vendor.id).length;
            }, 0);

            const totalValue = vendorOrders.reduce((sum, order) => {
                const orderQuotes = quotesData[order.id] || [];
                return sum + orderQuotes
                    .filter(q => q.vendorId === vendor.id)
                    .reduce((quoteSum, quote) => quoteSum + (parseFloat(quote.price) || 0), 0);
            }, 0);

            vendorPerformance[vendor.name] = {
                name: vendor.name,
                orders: vendorOrders.length,
                quotes: totalQuotes,
                totalValue: totalValue,
                avgValue: totalQuotes > 0 ? totalValue / totalQuotes : 0
            };
        });

        // Status distribution
        const statusDistribution = filteredOrders.reduce((acc, order) => {
            acc[order.status] = (acc[order.status] || 0) + 1;
            return acc;
        }, {});

        // Category performance (if categories exist) - only for in-progress orders
        const categoryPerformance = {};
        filteredOrders.forEach(order => {
            const category = order.category || 'Uncategorized';
            if (!categoryPerformance[category]) {
                categoryPerformance[category] = {
                    name: category,
                    orders: 0,
                    totalValue: 0,
                    completed: 0
                };
            }
            
            categoryPerformance[category].orders += 1;
            
            // Only add revenue for placed orders
            if (order.status === 'In Progress' || order.status === 'Completed') {
                const orderQuotes = quotesData[order.id] || [];
                const orderValue = orderQuotes.reduce((sum, quote) => 
                    sum + (parseFloat(quote.price) || 0), 0);
                categoryPerformance[category].totalValue += orderValue;
            }
            
            if (order.status === 'Completed') {
                categoryPerformance[category].completed += 1;
            }
        });

        // Key metrics - for placed orders (In Progress, Completed)
        const placedOrders = filteredOrders.filter(order => 
            order.status === 'In Progress' || 
            order.status === 'Completed'
        );
        
        const totalRevenue = placedOrders.reduce((sum, order) => {
            const orderQuotes = quotesData[order.id] || [];
            return sum + orderQuotes.reduce((quoteSum, quote) => 
                quoteSum + (parseFloat(quote.price) || 0), 0);
        }, 0);

        const totalOrders = filteredOrders.length;
        const placedOrderCount = placedOrders.length;
        const completedOrders = filteredOrders.filter(order => order.status === 'Completed').length;
        const avgOrderValue = placedOrderCount > 0 ? totalRevenue / placedOrderCount : 0;
        const completionRate = totalOrders > 0 ? (placedOrderCount / totalOrders) * 100 : 0;

        // Previous period comparison
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(startDate);
        const periodLength = (now - startDate) / (1000 * 60 * 60 * 24);
        previousStartDate.setDate(previousStartDate.getDate() - periodLength);
        previousEndDate.setDate(previousEndDate.getDate() - 1);

        const previousOrders = allOrders.filter(order => {
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
            return orderDate >= previousStartDate && orderDate <= previousEndDate;
        });

        const previousPlacedOrders = previousOrders.filter(order => 
            order.status === 'In Progress' || 
            order.status === 'Completed'
        );
        const previousRevenue = previousPlacedOrders.reduce((sum, order) => {
            const orderQuotes = quotesData[order.id] || [];
            return sum + orderQuotes.reduce((quoteSum, quote) => 
                quoteSum + (parseFloat(quote.price) || 0), 0);
        }, 0);

        const revenueGrowth = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;
        const orderGrowth = previousPlacedOrders.length > 0 ? ((placedOrderCount - previousPlacedOrders.length) / previousPlacedOrders.length) * 100 : 0;

        return {
            dailyData: Object.values(dailyData),
            vendorPerformance: Object.values(vendorPerformance).sort((a, b) => b.totalValue - a.totalValue),
            statusDistribution: Object.entries(statusDistribution).map(([status, count]) => ({
                name: status,
                value: count
            })),
            categoryPerformance: Object.values(categoryPerformance).sort((a, b) => b.totalValue - a.totalValue),
            metrics: {
                totalRevenue,
                totalOrders,
                placedOrderCount,
                completedOrders,
                avgOrderValue,
                completionRate,
                revenueGrowth,
                orderGrowth
            }
        };
    }, [allOrders, allVendors, dateRange, quotesData]);

    const renderMetricCard = (title, value, icon, growth, subtitle) => (
        <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                        {typeof value === 'number' && title.toLowerCase().includes('revenue') 
                            ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                            : typeof value === 'number' && title.toLowerCase().includes('rate')
                            ? `${value.toFixed(1)}%`
                            : typeof value === 'number'
                            ? value.toLocaleString('en-IN')
                            : value
                        }
                    </p>
                    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-full ${icon === TrendingUp ? 'bg-green-100' : icon === TrendingDown ? 'bg-red-100' : 'bg-blue-100'}`}>
                    {React.createElement(icon, { 
                        size: 24, 
                        className: icon === TrendingUp ? 'text-green-600' : icon === TrendingDown ? 'text-red-600' : 'text-blue-600' 
                    })}
                </div>
            </div>
            {growth !== undefined && (
                <div className="flex items-center mt-4">
                    {React.createElement(growth >= 0 ? TrendingUp : TrendingDown, { 
                        size: 16, 
                        className: growth >= 0 ? 'text-green-500' : 'text-red-500' 
                    })}
                    <span className={`text-sm font-medium ml-1 ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.abs(growth).toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-500 ml-1">vs previous period</span>
                </div>
            )}
        </div>
    );

    const renderChart = () => {
        const data = analyticsData.dailyData || [];
        
        if (chartType === 'line') {
            return (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
                        />
                        <YAxis />
                        <Tooltip 
                            labelFormatter={(value) => format(parseISO(value), 'MMM dd, yyyy')}
                            formatter={(value, name) => [
                                name === 'revenue' ? `₹${value.toLocaleString('en-IN')}` : value,
                                name.charAt(0).toUpperCase() + name.slice(1)
                            ]}
                        />
                        <Legend />
                        <Line 
                            type="monotone" 
                            dataKey={selectedMetric} 
                            stroke="#0088FE" 
                            strokeWidth={2}
                            dot={{ fill: '#0088FE', strokeWidth: 2, r: 4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            );
        } else if (chartType === 'area') {
            return (
                <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
                        />
                        <YAxis />
                        <Tooltip 
                            labelFormatter={(value) => format(parseISO(value), 'MMM dd, yyyy')}
                            formatter={(value, name) => [
                                name === 'revenue' ? `₹${value.toLocaleString('en-IN')}` : value,
                                name.charAt(0).toUpperCase() + name.slice(1)
                            ]}
                        />
                        <Legend />
                        <Area 
                            type="monotone" 
                            dataKey={selectedMetric} 
                            stroke="#0088FE" 
                            fill="#0088FE" 
                            fillOpacity={0.3}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            );
        } else if (chartType === 'bar') {
            return (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
                        />
                        <YAxis />
                        <Tooltip 
                            labelFormatter={(value) => format(parseISO(value), 'MMM dd, yyyy')}
                            formatter={(value, name) => [
                                name === 'revenue' ? `₹${value.toLocaleString('en-IN')}` : value,
                                name.charAt(0).toUpperCase() + name.slice(1)
                            ]}
                        />
                        <Legend />
                        <Bar dataKey={selectedMetric} fill="#0088FE" />
                    </BarChart>
                </ResponsiveContainer>
            );
        }
    };



    if (isLoadingQuotes) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Sales Analytics</h1>
                        <p className="text-gray-600">Comprehensive insights into your order performance</p>
                    </div>
                </div>

                {/* Loading Progress */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-center">
                        <div className="text-center">
                            <Activity className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading analytics data...</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Loading quotes for {loadedQuotesCount} of {totalQuotesToLoad} orders
                            </p>
                            {totalQuotesToLoad > 0 && (
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${(loadedQuotesCount / totalQuotesToLoad) * 100}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Show basic metrics while loading */}
                {analyticsData.metrics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {renderMetricCard(
                            'Total Orders',
                            analyticsData.metrics.totalOrders,
                            Package
                        )}
                        {renderMetricCard(
                            'Placed Orders',
                            analyticsData.metrics.placedOrderCount,
                            CheckCircle
                        )}
                        {renderMetricCard(
                            'Completed Orders',
                            analyticsData.metrics.completedOrders,
                            Award
                        )}
                        {renderMetricCard(
                            'Completion Rate',
                            analyticsData.metrics.completionRate,
                            Target
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (!analyticsData.metrics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Activity className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Sales Analytics</h1>
                    <p className="text-gray-600">Comprehensive insights into your order performance</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowVendorComparison(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                    >
                        <Users size={16} />
                        Compare Vendors
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'dashboard'
                                ? 'border-black text-black'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <BarChart3 size={16} />
                            Dashboard
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('detailed')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'detailed'
                                ? 'border-black text-black'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Activity size={16} />
                            Detailed Analytics
                        </div>
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'dashboard' ? (
                <AnalyticsDashboard allOrders={allOrders} allVendors={allVendors} currentUser={currentUser} />
            ) : (
                <div className="space-y-6">

            {/* Date Range Filter */}
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Date Range:</span>
                </div>
                <div className="relative" ref={dateRangeDropdownRef}>
                    <button 
                        onClick={() => setShowDateRangeDropdown(!showDateRangeDropdown)}
                        className="px-4 py-2 text-sm font-medium bg-transparent border-none outline-none transition-colors interactive-button text-black hover:text-gray-700"
                    >
                        {getCurrentDateRangeLabel()}
                    </button>
                    {showDateRangeDropdown && (
                        <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                            {dateRangeOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        setDateRange(option.value);
                                        setShowDateRangeDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${option.value === dateRange ? 'bg-gray-50 font-semibold' : ''}`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {renderMetricCard(
                    'Total Revenue (Placed Orders)',
                    analyticsData.metrics.totalRevenue,
                    DollarSign,
                    analyticsData.metrics.revenueGrowth
                )}
                {renderMetricCard(
                    'Placed Orders',
                    analyticsData.metrics.placedOrderCount,
                    Package,
                    analyticsData.metrics.orderGrowth
                )}
                {renderMetricCard(
                    'Average Order Value (Placed Orders)',
                    analyticsData.metrics.avgOrderValue,
                    Target,
                    undefined,
                    'Per placed order'
                )}
                {renderMetricCard(
                    'Completion Rate',
                    analyticsData.metrics.completionRate,
                    CheckCircle
                )}
            </div>

            {/* Main Chart */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Performance Trends</h2>
                    <div className="flex items-center gap-4">
                        <div className="relative" ref={metricDropdownRef}>
                            <button 
                                onClick={() => setShowMetricDropdown(!showMetricDropdown)}
                                className="px-4 py-2 text-sm font-medium bg-transparent border-none outline-none transition-colors interactive-button text-black hover:text-gray-700"
                            >
                                {getCurrentMetricLabel()}
                            </button>
                            {showMetricDropdown && (
                                <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                    {metricOptions.map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                setSelectedMetric(option.value);
                                                setShowMetricDropdown(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${option.value === selectedMetric ? 'bg-gray-50 font-semibold' : ''}`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex border border-gray-300 rounded-md">
                            <CustomTooltip text="Line Chart">
                                <button
                                    onClick={() => setChartType('line')}
                                    className={`px-3 py-2 text-sm transition-colors ${chartType === 'line' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <BarChart3 size={16} />
                                </button>
                            </CustomTooltip>
                            <CustomTooltip text="Area Chart">
                                <button
                                    onClick={() => setChartType('area')}
                                    className={`px-3 py-2 text-sm transition-colors ${chartType === 'area' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <Activity size={16} />
                                </button>
                            </CustomTooltip>
                            <CustomTooltip text="Bar Chart">
                                <button
                                    onClick={() => setChartType('bar')}
                                    className={`px-3 py-2 text-sm transition-colors ${chartType === 'bar' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <BarChart3 size={16} />
                                </button>
                            </CustomTooltip>
                        </div>
                    </div>
                </div>
                {renderChart()}
            </div>

            {/* Additional Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Vendor Performance */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Vendors by Revenue</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analyticsData.vendorPerformance.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                            <YAxis />
                            <Tooltip 
                                formatter={(value, name) => [
                                    name === 'totalValue' ? `₹${value.toLocaleString('en-IN')}` : value,
                                    name === 'totalValue' ? 'Revenue' : name.charAt(0).toUpperCase() + name.slice(1)
                                ]}
                            />
                            <Bar dataKey="totalValue" fill="#00C49F" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Status Distribution */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={analyticsData.statusDistribution}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {analyticsData.statusDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Category Performance */}
            {analyticsData.categoryPerformance.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Orders
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total Revenue
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Completed
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Completion Rate
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {analyticsData.categoryPerformance.map((category) => (
                                    <tr key={category.name}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {category.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {category.orders}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            ₹{category.totalValue.toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {category.completed}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {category.orders > 0 ? ((category.completed / category.orders) * 100).toFixed(1) : 0}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
                </div>
            )}
            
            {/* Vendor Comparison Modal */}
            {showVendorComparison && (
                <VendorComparisonModal
                    allVendors={allVendors}
                    allOrders={allOrders}
                    quotesData={quotesData}
                    onClose={() => setShowVendorComparison(false)}
                />
            )}
        </div>
    );
};

export default SalesAnalytics; 